import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  SectionHeader,
  SectionHeaderSemibold,
  PageSubheader,
  BodyText,
  MediumText,
} from "../../styles/Typography";
import ApplicationFooter from "../../components/Footer/ApplicationFooter";
import ImageGallery from "../../components/ImageGallery/ImageGallery";
import {
  IconInfoAccent,
  IconShieldAccent,
  IconCheckGreen,
} from "../../assets/icons/Icon";
import {
  Container,
  MainSection,
  Heading,
  HeadingContainer,
  BlackCard,
  CardsContainer,
  AccentCard,
  AccentCardText,
  ModulesSection,
  ModulesHeading,
  ModulesSubheader,
  SliderContainer,
  Slider,
  SliderButton,
  SlideContent,
  SlideImage,
  SlidePicture,
  SlideCaption,
  SlideCaptionContent,
  SlideTitle,
  SlideDescription,
  FeatureSection,
  FeaturesGrid,
  FeatureCard,
  FeatureCardHeading,
} from "./SoftwarePageTemplate.styles";

export interface Feature {
  name: string;
  icon: React.ElementType;
  text: string;
}

export interface SoftwareSlide {
  image: string;
  alt: string;
  title: string;
  description: string;
}

interface PageProps {
  id: string;
  title: string;
  subtitle: string;
  cardText: string;
  accentText1: string;
  accentText2: string;
  moduleSubheader: string;
  slides: SoftwareSlide[];
  features: Feature[];
}

const SoftwarePageTemplate: React.FC<PageProps> = ({
  id,
  title,
  subtitle,
  cardText,
  accentText1,
  accentText2,
  moduleSubheader,
  slides,
  features,
}) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<1 | -1>(1);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (activeSlide >= slides.length) {
      setActiveSlide(0);
      setPreviousSlide(null);
    }
  }, [activeSlide, slides.length]);

  useEffect(
    () => () => {
      if (transitionTimer.current) {
        clearTimeout(transitionTimer.current);
      }
    },
    []
  );

  const changeSlide = (direction: 1 | -1) => {
    if (slides.length < 2) {
      return;
    }

    const nextSlide = (activeSlide + direction + slides.length) % slides.length;

    setSlideDirection(direction);
    setPreviousSlide(activeSlide);
    setActiveSlide(nextSlide);

    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
    }

    transitionTimer.current = setTimeout(() => {
      setPreviousSlide(null);
    }, 500);
  };

  const currentSlide = slides[activeSlide];
  const galleryImages = useMemo(
    () => slides.map(({ image, alt }) => ({ src: image, alt })),
    [slides]
  );

  return (
    <Container>
      <MainSection id={id}>
        <HeadingContainer>
          <Heading>
            <SectionHeader>{title}</SectionHeader>
            <PageSubheader>{subtitle}</PageSubheader>
          </Heading>
          <BlackCard>
            <span>
              <IconCheckGreen />
            </span>
            <BodyText>{cardText}</BodyText>
          </BlackCard>
        </HeadingContainer>
        <CardsContainer>
          <AccentCard>
            <span>
              <IconInfoAccent />
            </span>
            <AccentCardText>{accentText1}</AccentCardText>
          </AccentCard>
          <AccentCard>
            <span>
              <IconShieldAccent />
            </span>
            <AccentCardText>{accentText2}</AccentCardText>
          </AccentCard>
        </CardsContainer>
      </MainSection>

      <ModulesSection id="modules">
        <ModulesHeading>
          <SectionHeaderSemibold>Модули системы</SectionHeaderSemibold>
          <ModulesSubheader>
            <BodyText>{moduleSubheader}</BodyText>
          </ModulesSubheader>
        </ModulesHeading>

        {currentSlide && (
          <SliderContainer>
            <Slider aria-roledescription="carousel" aria-label="Модули CemLife">
              <SliderButton
                type="button"
                onClick={() => changeSlide(-1)}
                disabled={slides.length < 2}
                aria-label="Предыдущий модуль"
              >
                <i className="pi pi-chevron-left" aria-hidden="true" />
              </SliderButton>

              <SlideContent
                aria-label={`${activeSlide + 1} из ${slides.length}`}
              >
                <ImageGallery
                  images={galleryImages}
                  initialIndex={activeSlide}
                  ariaLabel="Галерея модулей системы"
                >
                  {({ openGallery }) => (
                    <SlideImage
                      type="button"
                      onClick={() => openGallery(activeSlide)}
                      aria-label={`Открыть изображение «${currentSlide.title}» в галерее`}
                    >
                      {slides.map((slide, index) => {
                        const slideState =
                          index === activeSlide
                            ? "active"
                            : index === previousSlide
                            ? "previous"
                            : "inactive";

                        return (
                          <SlidePicture
                            key={`${slide.image}-${slide.title}`}
                            $state={slideState}
                            $direction={slideDirection}
                            aria-hidden={slideState !== "active"}
                          >
                            <img
                              src={slide.image}
                              alt={slide.alt}
                              loading="lazy"
                              decoding="async"
                            />
                          </SlidePicture>
                        );
                      })}
                    </SlideImage>
                  )}
                </ImageGallery>
                <SlideCaption aria-live="polite">
                  {slides.map((slide, index) => (
                    <SlideCaptionContent
                      key={`${slide.title}-${index}`}
                      $active={index === activeSlide}
                      aria-hidden={index !== activeSlide}
                    >
                      <SlideTitle>{slide.title}</SlideTitle>
                      <SlideDescription>{slide.description}</SlideDescription>
                    </SlideCaptionContent>
                  ))}
                </SlideCaption>
              </SlideContent>

              <SliderButton
                type="button"
                onClick={() => changeSlide(1)}
                disabled={slides.length < 2}
                aria-label="Следующий модуль"
              >
                <i className="pi pi-chevron-right" aria-hidden="true" />
              </SliderButton>
            </Slider>
          </SliderContainer>
        )}
      </ModulesSection>

      <FeatureSection id="features">
        <SectionHeaderSemibold>Особенности системы</SectionHeaderSemibold>
        <FeaturesGrid>
          {features.map((feature) => {
            const FeatureIcon = feature.icon;

            return (
              <FeatureCard key={feature.name} id={feature.name}>
                <FeatureCardHeading>
                  <span>
                    <FeatureIcon aria-hidden="true" focusable="false" />
                  </span>
                  <p>{feature.name}</p>
                </FeatureCardHeading>
                <MediumText>{feature.text}</MediumText>
              </FeatureCard>
            );
          })}
        </FeaturesGrid>
      </FeatureSection>

      <ApplicationFooter />
    </Container>
  );
};

export default SoftwarePageTemplate;
